import React, { useState } from 'react';
import {
  FaSearch,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
  FaMapMarkerAlt
} from 'react-icons/fa';
import { QUERY_STEPS } from '../../data/catalogs.constants';
import { DEMO_RECORD_KEYS } from '../../data/cadastral-registry.data';
import { formatInt } from '../../services/cadastral.service';
import styles from '../../styles/Cadastral.module.css';

export default function CadastralSearch({
  onSearch,
  onOpenRecord,
  isLoading,
  currentStep,
  progressPct,
  searchStatus, // null | 'ok' | 'not_found' | 'invalid_format' | 'no_geo'
  statusInfo,
  totalRecords = 498
}) {
  const [searchKey, setSearchKey] = useState('');

  const handleChange = event => {
    const inputValue = event.target.value.replace(/\D/g, '').slice(0, 15);
    setSearchKey(inputValue);
  };

  const handleClear = () => {
    setSearchKey('');
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (searchKey.trim()) {
      onSearch(searchKey.trim());
    }
  };

  const handleSelectChip = exampleKey => {
    setSearchKey(exampleKey);
    onSearch(exampleKey);
  };

  const chips = [
    { recordKey: DEMO_RECORD_KEYS[0] || '140109001025005', label: '140109001025005', title: 'Predio Urbano Edificado' },
    { recordKey: DEMO_RECORD_KEYS[1] || '140100139061009', label: '140100139061009', title: 'Predio Urbano Baldío' },
    { recordKey: DEMO_RECORD_KEYS[2] || '140505255416113', label: '140505255416113', title: 'Predio Rústico' },
    { recordKey: DEMO_RECORD_KEYS[3] || '140108665248831', label: '140108665248831', title: 'Predio sin georreferencia' },
    { recordKey: '999999999999999', label: '999999999999999', title: 'No existe en el padrón', bad: true },
    { recordKey: '1401', label: '1401', title: 'Formato incompleto', bad: true }
  ];

  return (
    <>
      <div className={styles.searchFloat} id="searchFloat">
        <form
          className={`${styles.sfRow} ${searchStatus === 'invalid_format' ? styles.hasError : ''}`}
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          <FaSearch className={styles.sfIco} size={16} />
          <input
            id="cadastralKeyInput"
            type="text"
            inputMode="numeric"
            maxLength={15}
            placeholder="Buscar clave catastral (15 dígitos)"
            aria-label="Clave catastral"
            value={searchKey}
            onChange={handleChange}
            disabled={isLoading}
          />
          <span className={styles.counter}>{searchKey.length}/15</span>

          {searchKey.length > 0 && (
            <button
              type="button"
              className={styles.sfClear}
              onClick={handleClear}
              title="Limpiar"
              disabled={isLoading}
            >
              <FaTimes size={13} />
            </button>
          )}

          <button
            type="submit"
            className={styles.sfGo}
            title="Consultar clave catastral"
            disabled={isLoading || searchKey.length === 0}
          >
            <FaSearch size={16} />
          </button>
        </form>

        {/* Status, loader, and alerts dropdown. */}
        {isLoading && (
          <div className={styles.sfDrop}>
            <div className={styles.sfLoading}>
              <div className={styles.sflHead}>
                <strong>Consultando servicios de Catastro</strong>
                <span>{progressPct}%</span>
              </div>
              <div className={styles.sfBar}>
                <i style={{ width: `${progressPct}%` }} />
              </div>
              <ul className={styles.steps}>
                {QUERY_STEPS.map((step, index) => {
                  const isDoing = index === currentStep;
                  const isDone = index < currentStep;
                  return (
                    <li
                      key={index}
                      className={isDoing ? styles.isDoing : isDone ? styles.isDone : ''}
                    >
                      <span className={styles.stIco}>
                        <FaCheck size={9} />
                      </span>
                      <span>{step}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {!isLoading && searchStatus === 'ok' && statusInfo && (
          <div className={styles.sfDrop}>
            <div className={`${styles.sfMsg} ${styles.sfMsgOk}`}>
              <span className={styles.sfmIco}><FaMapMarkerAlt size={18} /></span>
              <div className={styles.sfmBody}>
                <strong>Predio localizado</strong>
                <p>
                  {statusInfo.direccion || 'Sin domicilio registrado'}
                  {statusInfo.colonia ? ` · ${statusInfo.colonia}` : ''}
                  <br />
                  Haz clic en el punto del mapa o abre el expediente directamente.
                </p>
                <div className={styles.sfmAct}>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    onClick={onOpenRecord}
                  >
                    Abrir expediente
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLoading && searchStatus === 'no_geo' && statusInfo && (
          <div className={styles.sfDrop}>
            <div className={`${styles.sfMsg} ${styles.sfMsgWarn}`}>
              <span className={styles.sfmIco}><FaExclamationTriangle size={18} /></span>
              <div className={styles.sfmBody}>
                <strong>Predio sin georreferencia</strong>
                <p>
                  El padrón no cuenta con coordenadas para <code>{statusInfo.clave}</code>,
                  por lo que no puede ubicarse en el mapa. La información alfanumérica sí está disponible.
                </p>
                <div className={styles.sfmAct}>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    onClick={onOpenRecord}
                  >
                    Ver expediente
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLoading && searchStatus === 'not_found' && statusInfo && (
          <div className={styles.sfDrop}>
            <div className={`${styles.sfMsg} ${styles.sfMsgWarn}`}>
              <span className={styles.sfmIco}><FaExclamationTriangle size={18} /></span>
              <div className={styles.sfmBody}>
                <strong>No existe información al respecto</strong>
                <p>
                  El servicio de Catastro respondió correctamente, pero <strong>no se encontraron registros</strong> para la clave <code>{statusInfo.clave}</code>.<br />
                  Respuesta: <code>HTTP 200 · 0 registros</code>
                </p>
                <div className={styles.sfmAct}>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => setSearchKey('')}
                  >
                    Consultar otra clave
                  </button>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    onClick={() => handleSelectChip(DEMO_RECORD_KEYS[0] || '140109001025005')}
                  >
                    Usar clave de ejemplo
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLoading && searchStatus === 'invalid_format' && statusInfo && (
          <div className={styles.sfDrop}>
            <div className={`${styles.sfMsg} ${styles.sfMsgErr}`}>
              <span className={styles.sfmIco}><FaTimes size={18} /></span>
              <div className={styles.sfmBody}>
                <strong>{statusInfo.titulo}</strong>
                <p>{statusInfo.mensaje}</p>
              </div>
            </div>
          </div>
        )}

        {/* Example chips. */}
        <div className={styles.sfExamples}>
          <span className={styles.lbl}>Ejemplos</span>
          <div className={styles.sfChips}>
            {chips.map(item => (
              <button
                key={item.recordKey}
                type="button"
                className={`${styles.chip} ${item.bad ? styles.chipBad : ''}`}
                title={item.title}
                onClick={() => handleSelectChip(item.recordKey)}
                disabled={isLoading}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom service status. */}
      <div className={styles.wsFloat}>
        <span className={styles.dotLive} />
        WS Catastro Querétaro · <strong>PRODUCCIÓN</strong>
        <span className={styles.wsSep} />
        <span>{formatInt(totalRecords)}</span> predios
      </div>
    </>
  );
}
